import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("btt_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Admin endpoints return 404 (not 401) when the token belongs to a non-admin — this
// hides admin surface from parents. That means a stale/parent token on an admin page
// looks like a "Not found" error. Detect it here and force a clean re-login so the
// user doesn't get stuck seeing "Request failed with status code 404" on save.
// For parent routes: an expired JWT returns 401 "Token expired" — treat that the same
// (kick to /login?stale=1) so slow enrollment sessions don't die with a tiny red error.
// Once we trigger a full-page redirect for a stale/expired session, return a never-resolving
// Promise instead of rejecting. Callers' unhandled rejections would otherwise flash the raw
// axios error in the CRA dev overlay (and pollute Sentry-style loggers in prod) while the
// redirect is still in flight. Nothing after this point on the current page needs to run.
const NEVER = new Promise(() => {});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (typeof window === "undefined") return Promise.reject(err);
    const path = window.location.pathname;
    const onAdmin = path.startsWith("/admin");
    const onGuest = path === "/" || path === "/login" || path === "/register" || path.startsWith("/library");

    if (url.includes("/admin/") && (status === 401 || (status === 404 && detail === "Not found"))) {
      if (onAdmin) {
        localStorage.removeItem("btt_token");
        if (path !== "/admin/login") {
          window.location.replace("/admin/login?stale=1");
          return NEVER; // swallow error so callers don't flash a red overlay mid-redirect
        }
      }
    } else if (status === 401 && !onGuest && !onAdmin) {
      // Parent JWT expired / invalid — clear + kick to login with a friendly banner.
      // Skip login/register/landing/library (public) so the redirect never loops.
      const isExpiry = detail === "Token expired" || detail === "Invalid token" || detail === "Not authenticated";
      if (isExpiry) {
        localStorage.removeItem("btt_token");
        window.location.replace("/login?stale=1");
        return NEVER; // swallow error so callers don't flash a red overlay mid-redirect
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  const status = err?.response?.status;
  const url = err?.config?.url || "";
  // Friendlier message for the stale-admin-token case (see interceptor above).
  if (url.includes("/admin/") && status === 404 && d === "Not found") {
    return "Your admin session is stale — please sign in again.";
  }
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  // Dict detail — surface a human `message` field if present (e.g. slot_limit_reached).
  if (typeof d === "object" && typeof d.message === "string") return d.message;
  try { return JSON.stringify(d); } catch { return "Something went wrong"; }
}

export default api;
